/**
 * Geohash encoding utility
 * Converts latitude/longitude coordinates into a geohash string
 */

const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Encode latitude/longitude to geohash
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate
 * @param {number} precision - Length of resulting geohash (default: 9)
 * @returns {string} Geohash string
 */
export function encodeGeohash(latitude, longitude, precision = 9) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    throw new Error('Latitude and longitude must be numbers');
  }

  if (latitude < -90 || latitude > 90) {
    throw new Error('Latitude must be between -90 and 90');
  }

  if (longitude < -180 || longitude > 180) {
    throw new Error('Longitude must be between -180 and 180');
  }

  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let geohash = '';

  let latMin = -90;
  let latMax = 90;
  let lonMin = -180;
  let lonMax = 180;

  while (geohash.length < precision) {
    if (evenBit) {

      const lonMid = (lonMin + lonMax) / 2;
      if (longitude >= lonMid) {
        idx |= (1 << (4 - bit));
        lonMin = lonMid;
      } else {
        lonMax = lonMid;
      }
    } else {

      const latMid = (latMin + latMax) / 2;
      if (latitude >= latMid) {
        idx |= (1 << (4 - bit));
        latMin = latMid;
      } else {
        latMax = latMid;
      }
    }

    evenBit = !evenBit;

    if (bit < 4) {
      bit++;
    } else {
      geohash += BASE32[idx];
      bit = 0;
      idx = 0;
    }
  }

  return geohash;
}

/**
 * Decode geohash to latitude/longitude bounds
 * @param {string} geohash - Geohash string
 * @returns {object} Object containing latitude/longitude bounds and center point
 */
export function decodeGeohash(geohash) {
  if (typeof geohash !== 'string' || geohash.length === 0) {
    throw new Error('Geohash must be a non-empty string');
  }

  let evenBit = true;
  let latMin = -90;
  let latMax = 90;
  let lonMin = -180;
  let lonMax = 180;

  for (let i = 0; i < geohash.length; i++) {
    const chr = geohash[i];
    const idx = BASE32.indexOf(chr);

    if (idx === -1) {
      throw new Error(`Invalid geohash character: ${chr}`);
    }

    for (let n = 4; n >= 0; n--) {
      const bitN = (idx >> n) & 1;

      if (evenBit) {

        const lonMid = (lonMin + lonMax) / 2;
        if (bitN === 1) {
          lonMin = lonMid;
        } else {
          lonMax = lonMid;
        }
      } else {

        const latMid = (latMin + latMax) / 2;
        if (bitN === 1) {
          latMin = latMid;
        } else {
          latMax = latMid;
        }
      }

      evenBit = !evenBit;
    }
  }

  return {
    latitude: {
      min: latMin,
      max: latMax,
      center: (latMin + latMax) / 2
    },
    longitude: {
      min: lonMin,
      max: lonMax,
      center: (lonMin + lonMax) / 2
    }
  };
}
